import axios from "axios";
import React, { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { FaEye } from "react-icons/fa6";
import { ApiUrl } from "Services/Services";
import { useNavigate } from "react-router-dom";

function Users() {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("users"); // "users" | "walkins"
  const usersPerPage = 5;
  const navigate = useNavigate();

  const totalPages = Math.ceil(users.length / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);

  // Fetch users or walk-in customers
  const fetchData = async () => {
    try {
      let url =
        activeTab === "users"
          ? `${ApiUrl}/auth/users`
          : `${ApiUrl}/auth/walkinusers`;
      const response = await axios.get(url);
      setUsers(response.data.data || []);
    } catch (error) {
      console.log(error.message);
      toast.error("Failed to fetch data");
    }
  };

  useEffect(() => {
    fetchData();
    setCurrentPage(1); // reset pagination on tab switch
  }, [activeTab]);

  const handleDelete = async (id) => {
    try {
      let url =
        activeTab === "users"
          ? `${ApiUrl}/auth/users/delete/${id}`
          : `${ApiUrl}/auth/walkinusers/delete/${id}`;

      await axios.delete(url);
      toast.success("User Deleted");
      fetchData();
    } catch (error) {
      console.log(error.message);
      toast.warn(error.message);
    }
  };

  const handleView = (id) => {
    if (activeTab === "users") {
      navigate(`/admin/viewUser/${id}`);
    } else {
      navigate(`/admin/viewWalkinUser/${id}`);
    }
  };

  return (
    <div className="p-6">
      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab("users")}
          className={`px-4 py-2 ${
            activeTab === "users"
              ? "border-b-2 border-blue-500 text-blue-600 font-medium"
              : "text-gray-600 hover:text-blue-500"
          }`}
        >
          Users
        </button>
        <button
          onClick={() => setActiveTab("walkins")}
          className={`px-4 py-2 ${
            activeTab === "walkins"
              ? "border-b-2 border-blue-500 text-blue-600 font-medium"
              : "text-gray-600 hover:text-blue-500"
          }`}
        >
          Walk-in Customers
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white shadow-md rounded-lg">
        <table className="min-w-full border rounded-lg bg-white">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-6 py-4">#</th>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Phone</th>
              <th className="px-6 py-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user, index) => (
              <tr
                key={user.id}
                className="border-b hover:bg-gray-50 transition-all"
              >
                <td className="px-6 py-4 font-medium text-gray-900">
                  {(currentPage - 1) * usersPerPage + index + 1}
                </td>
                <td className="px-6 py-4">{user?.name}</td>
                <td className="px-6 py-4">{user?.email || "-"}</td>
                <td className="px-6 py-4">{user?.phoneNumber || user?.phone ||  "-"}</td>
                <td className="px-6 py-4 flex items-center justify-center gap-4">
                  <button
                    onClick={() => handleView(user?.id)}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <FaEye />
                  </button>
                  <button
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDelete(user?.id)}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
            {currentUsers.length === 0 && (
              <tr>
                <td
                  colSpan="5"
                  className="text-center text-gray-500 py-6 italic"
                >
                  No {activeTab === "users" ? "users" : "walk-in customers"}{" "}
                  found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Previous
        </button>

        <div className="text-sm text-gray-600">
          Page {currentPage} of {totalPages || 1}
        </div>

        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default Users;
